import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { 
  Plus, FolderOpen, FolderClosed, Briefcase, MoreVertical, Clock,
  CheckCircle, AlertCircle, Trash2, ChevronDown, ChevronRight, Pencil, Brain
} from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { cn } from '../lib/utils';

const statusConfig = {
  draft: { label: 'Borrador', icon: AlertCircle, color: 'text-muted-foreground' },
  in_review: { label: 'En Revisión', icon: Clock, color: 'text-warning' },
  finalized: { label: 'Finalizado', icon: CheckCircle, color: 'text-success' }
};

export const HomePage = () => {
  const navigate = useNavigate();
  const { projects, folders, createProject, createFolder, updateFolder, deleteProject, deleteFolder, setCurrentProject, fetchProjects, fetchFolders, updateProject } = useProject();
  const { user } = useAuth();

  const [newProjectDialog, setNewProjectDialog] = useState(false);
  const [newFolderDialog, setNewFolderDialog] = useState(false);
  const [renameFolderDialog, setRenameFolderDialog] = useState(false);
  const [moveProjectDialog, setMoveProjectDialog] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null); // { id, name, currentFolderId }
  const [moveTargetFolder, setMoveTargetFolder] = useState(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderId, setRenameFolderId] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [projectTargetFolder, setProjectTargetFolder] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'folder'|'project', id, name }

  // Auto-expand all folders on first load
  useEffect(() => {
    if (folders.length > 0 && Object.keys(expandedFolders).length === 0) {
      const initial = {};
      folders.forEach(f => { initial[f.id] = true; });
      setExpandedFolders(initial);
    }
  }, [folders]);

  const toggleFolder = (folderId) => {
    setExpandedFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast.error('Ingresa un nombre para el proyecto');
      return;
    }
    if (!projectTargetFolder) {
      toast.error('Selecciona una carpeta para el proyecto');
      return;
    }
    try {
      const project = await createProject({ 
        name: newProjectName,
        folder_id: projectTargetFolder
      });
      setCurrentProject(project);
      toast.success('Proyecto creado');
      setNewProjectDialog(false);
      setNewProjectName('');
      setProjectTargetFolder(null);
      navigate(`/app/project/${project.id}/specifications`);
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Error al crear el proyecto');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Ingresa un nombre para la carpeta');
      return;
    }
    try {
      const folder = await createFolder({ name: newFolderName });
      toast.success(`Carpeta "${folder.name}" creada`);
      setExpandedFolders(prev => ({ ...prev, [folder.id]: true }));
      setNewFolderDialog(false);
      setNewFolderName('');
    } catch (err) {
      toast.error('Error al crear la carpeta');
    }
  };

  const handleRenameFolder = async () => {
    if (!renameFolderName.trim()) return;
    try {
      await updateFolder(renameFolderId, { name: renameFolderName });
      toast.success('Carpeta renombrada');
      setRenameFolderDialog(false);
    } catch (err) {
      toast.error('Error al renombrar');
    }
  };

  const handleDeleteFolder = (folderId) => {
    const folderProjects = projects.filter(p => p.folder_id === folderId);
    if (folderProjects.length > 0) {
      toast.error('No puedes eliminar una carpeta con proyectos. Mueve o elimina los proyectos primero.');
      return;
    }
    const folder = folders.find(f => f.id === folderId);
    setDeleteTarget({ type: 'folder', id: folderId, name: folder?.name || 'Carpeta' });
  };

  const handleOpenProject = (project) => {
    setCurrentProject(project);
    navigate(`/app/project/${project.id}/dashboard`);
  };

  const handleDeleteProject = (projectId, e) => {
    e.stopPropagation();
    const project = projects.find(p => p.id === projectId);
    setDeleteTarget({ type: 'project', id: projectId, name: project?.name || 'Proyecto' });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'folder') {
        await deleteFolder(deleteTarget.id);
        toast.success('Carpeta eliminada');
      } else {
        await deleteProject(deleteTarget.id);
        toast.success('Proyecto eliminado');
      }
    } catch (err) {
      toast.error('Error al eliminar');
    } finally {
      setDeleteTarget(null);
    }
  };

  const openNewProjectInFolder = (folderId) => {
    setProjectTargetFolder(folderId);
    setNewProjectDialog(true);
  };

  const startMoveProject = (project, e) => {
    if (e) e.stopPropagation();
    setMoveTarget({ id: project.id, name: project.name, currentFolderId: project.folder_id });
    setMoveTargetFolder(null);
    setMoveProjectDialog(true);
  };

  const handleMoveProject = async () => {
    if (!moveTarget) return;
    try {
      await updateProject(moveTarget.id, { folder_id: moveTargetFolder });
      toast.success(moveTargetFolder 
        ? `Proyecto movido a "${folders.find(f => f.id === moveTargetFolder)?.name}"` 
        : 'Proyecto movido fuera de carpeta');
      setMoveProjectDialog(false);
      setMoveTarget(null);
      fetchProjects();
    } catch (err) {
      toast.error('Error al mover el proyecto');
    }
  };

  const startRename = (folder) => {
    setRenameFolderId(folder.id);
    setRenameFolderName(folder.name);
    setRenameFolderDialog(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="home-page">
      {/* Welcome */}
      <Card className="bg-gradient-to-r from-card to-secondary/30 border-border/50">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold mb-2">Bienvenido, {user?.name || 'Analista'}</h2>
              <p className="text-muted-foreground text-lg">
                Organiza tus proyectos en carpetas para mantener todo en orden.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                className="gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                onClick={() => navigate('/app/ai-analyst')}
                data-testid="ai-analyst-btn"
              >
                <Brain className="w-4 h-4" />
                Analista IA
              </Button>
              <Button variant="secondary" className="gap-2" onClick={() => setNewFolderDialog(true)} data-testid="new-folder-btn">
                <FolderOpen className="w-4 h-4" />
                Nueva Carpeta
              </Button>
              <Button className="btn-glow gap-2" onClick={() => setNewProjectDialog(true)} data-testid="new-project-btn">
                <Plus className="w-4 h-4" />
                Nuevo Proyecto
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Folders */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <FolderOpen className="w-5 h-5 text-primary" />
          <h3 className="text-xl font-semibold">Mis Carpetas</h3>
          <span className="text-sm text-muted-foreground">({folders.length})</span>
        </div>

        {folders.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-10 text-center">
              <FolderClosed className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <h4 className="text-lg font-semibold mb-2">Sin carpetas</h4>
              <p className="text-muted-foreground mb-4">Crea tu primera carpeta para organizar proyectos</p>
              <Button onClick={() => setNewFolderDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crear Carpeta
              </Button>
            </CardContent>
          </Card>
        ) : (
          folders.map(folder => {
            const folderProjects = projects.filter(p => p.folder_id === folder.id)
              .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
            const isExpanded = expandedFolders[folder.id];

            return (
              <Card key={folder.id} className="overflow-hidden" data-testid={`folder-card-${folder.id}`}>
                {/* Folder header */}
                <div
                  className="w-full flex items-center gap-3 p-4 hover:bg-secondary/30 transition-colors text-left cursor-pointer"
                  onClick={() => toggleFolder(folder.id)}
                  role="button"
                  data-testid={`folder-toggle-${folder.id}`}
                >
                  {isExpanded 
                    ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> 
                    : <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  }
                  {isExpanded 
                    ? <FolderOpen className="w-5 h-5 text-primary" /> 
                    : <FolderClosed className="w-5 h-5 text-muted-foreground" />
                  }
                  <span className="font-semibold flex-1">{folder.name}</span>
                  <span className="text-xs text-muted-foreground mr-2">
                    {folderProjects.length} proyecto{folderProjects.length !== 1 ? 's' : ''}
                  </span>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" data-testid={`folder-menu-${folder.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openNewProjectInFolder(folder.id); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nuevo Proyecto Aqui
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); startRename(folder); }}>
                        <Pencil className="w-4 h-4 mr-2" />
                        Renombrar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }} className="text-destructive focus:text-destructive">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar Carpeta
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Expanded projects */}
                {isExpanded && (
                  <div className="border-t border-border/30 bg-secondary/10">
                    {folderProjects.length === 0 ? (
                      <div className="p-6 text-center">
                        <p className="text-sm text-muted-foreground mb-3">Carpeta vacia</p>
                        <Button variant="outline" size="sm" onClick={() => openNewProjectInFolder(folder.id)}>
                          <Plus className="w-3 h-3 mr-1" />
                          Crear Proyecto
                        </Button>
                      </div>
                    ) : (
                      <div className="p-3 grid grid-cols-3 gap-3">
                        {folderProjects.map(project => {
                          const Status = statusConfig[project.status] || statusConfig.draft;
                          return (
                            <Card 
                              key={project.id}
                              className="card-hover cursor-pointer overflow-hidden"
                              onClick={() => handleOpenProject(project)}
                              data-testid={`project-card-${project.id}`}
                            >
                              <div className="h-20 bg-gradient-to-br from-secondary to-primary/10 relative">
                                {project.status === 'finalized' && (
                                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-success/20 text-success text-xs font-medium rounded">
                                    FINALIZADO
                                  </span>
                                )}
                                {project.is_demo && (
                                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded">
                                    DEMO
                                  </span>
                                )}
                                <div className="absolute bottom-2 right-2 w-9 h-9 rounded-lg bg-card flex items-center justify-center">
                                  {project.specifications?.logo ? (
                                    <img src={project.specifications.logo} alt="" className="w-7 h-7 rounded object-cover" />
                                  ) : (
                                    <Briefcase className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </div>
                              </div>
                              <CardContent className="p-3">
                                <div className="flex items-start justify-between mb-1">
                                  <h4 className="font-semibold text-sm line-clamp-1">{project.name}</h4>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                                        <MoreVertical className="w-3 h-3" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); startMoveProject(project, e); }}>
                                        <FolderOpen className="w-4 h-4 mr-2" />
                                        Mover a carpeta
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={(e) => handleDeleteProject(project.id, e)} className="text-destructive focus:text-destructive">
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Eliminar
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(project.updated_at).toLocaleDateString('es-MX')}
                                  </span>
                                  <span className={cn("flex items-center gap-1", Status.color)}>
                                    <Status.icon className="w-3 h-3" />
                                    {Status.label}
                                  </span>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                        
                        {/* Add project card */}
                        <Card 
                          className="border-dashed cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors flex items-center justify-center min-h-[130px]"
                          onClick={() => openNewProjectInFolder(folder.id)}
                          data-testid={`add-project-${folder.id}`}
                        >
                          <div className="text-center p-4">
                            <Plus className="w-6 h-6 text-muted-foreground mx-auto mb-1" />
                            <p className="text-xs text-muted-foreground">Nuevo Proyecto</p>
                          </div>
                        </Card>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })
        )}
      </div>

      {/* Projects without folder (e.g., demo) */}
      {(() => {
        const folderIds = new Set(folders.map(f => f.id));
        const unfiledProjects = projects.filter(p => !p.folder_id || !folderIds.has(p.folder_id));
        if (unfiledProjects.length === 0) return null;
        return (
          <div className="space-y-3 mt-6" data-testid="unfiled-projects-section">
            <div className="grid grid-cols-3 gap-3">
              {unfiledProjects.map(project => {
                const Status = statusConfig[project.status] || statusConfig.draft;
                return (
                  <Card 
                    key={project.id}
                    className="card-hover cursor-pointer overflow-hidden"
                    onClick={() => handleOpenProject(project)}
                    data-testid={`project-card-${project.id}`}
                  >
                    <div className="h-20 bg-gradient-to-br from-secondary to-primary/10 relative">
                      {project.is_demo && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded">
                          DEMO
                        </span>
                      )}
                      <div className="absolute bottom-2 right-2 w-9 h-9 rounded-lg bg-card flex items-center justify-center">
                        {project.specifications?.logo ? (
                          <img src={project.specifications.logo} alt="" className="w-7 h-7 rounded object-cover" />
                        ) : (
                          <Briefcase className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="font-semibold text-sm line-clamp-1">{project.name}</h4>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                              <MoreVertical className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); startMoveProject(project, e); }}>
                              <FolderOpen className="w-4 h-4 mr-2" />
                              Mover a carpeta
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => handleDeleteProject(project.id, e)} className="text-destructive focus:text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(project.updated_at).toLocaleDateString('es-MX')}
                        </span>
                        <span className={cn("flex items-center gap-1", Status.color)}>
                          <Status.icon className="w-3 h-3" />
                          {Status.label}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Create Project Dialog */}
      <Dialog open={newProjectDialog} onOpenChange={setNewProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nuevo Proyecto</DialogTitle>
            <DialogDescription>Ingresa el nombre y selecciona la carpeta</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Nombre del proyecto</label>
              <Input 
                placeholder="Ej: Mi Cafetería"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                data-testid="new-project-name-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Carpeta *</label>
              {folders.length === 0 ? (
                <p className="text-sm text-muted-foreground">Crea una carpeta primero para organizar tu proyecto.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {folders.map(folder => (
                    <Button
                      key={folder.id}
                      variant={projectTargetFolder === folder.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setProjectTargetFolder(folder.id)}
                      className="gap-2 justify-start"
                      data-testid={`folder-select-${folder.id}`}
                    >
                      <FolderOpen className="w-4 h-4" />
                      {folder.name}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNewProjectDialog(false); setNewProjectName(''); setProjectTargetFolder(null); }}>
              Cancelar
            </Button>
            <Button onClick={handleCreateProject} disabled={!newProjectName.trim() || !projectTargetFolder} data-testid="create-project-btn">
              Crear Proyecto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={newFolderDialog} onOpenChange={setNewFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nueva Carpeta</DialogTitle>
            <DialogDescription>Organiza tus proyectos en carpetas</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input 
              placeholder="Nombre de la carpeta"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              data-testid="new-folder-name-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNewFolderDialog(false); setNewFolderName(''); }}>
              Cancelar
            </Button>
            <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()} data-testid="create-folder-btn">
              Crear Carpeta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={renameFolderDialog} onOpenChange={setRenameFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renombrar Carpeta</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input 
              placeholder="Nuevo nombre"
              value={renameFolderName}
              onChange={(e) => setRenameFolderName(e.target.value)}
              data-testid="rename-folder-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameFolderDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleRenameFolder} disabled={!renameFolderName.trim()} data-testid="rename-folder-btn">
              Renombrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              Confirmar eliminacion
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.type === 'folder'
                ? `¿Estas seguro de que deseas eliminar la carpeta "${deleteTarget?.name}"? Esta accion no se puede deshacer.`
                : `¿Estas seguro de que deseas eliminar el proyecto "${deleteTarget?.name}"? Se perderan todos los datos del proyecto permanentemente.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 my-2">
            <p className="text-sm text-destructive flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Ten cuidado, esta accion es irreversible.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="delete-cancel-btn">
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="delete-confirm-btn">
              Si, Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Project Dialog */}
      <Dialog open={moveProjectDialog} onOpenChange={(open) => { if (!open) { setMoveProjectDialog(false); setMoveTarget(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-primary" />
              Mover proyecto
            </DialogTitle>
            <DialogDescription>
              Selecciona la carpeta destino para "{moveTarget?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2">
            {folders.map(folder => (
              <Button
                key={folder.id}
                variant={moveTargetFolder === folder.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMoveTargetFolder(folder.id)}
                disabled={folder.id === moveTarget?.currentFolderId}
                className="w-full gap-2 justify-start"
                data-testid={`move-folder-${folder.id}`}
              >
                <FolderOpen className="w-4 h-4" />
                {folder.name}
                {folder.id === moveTarget?.currentFolderId && (
                  <span className="text-xs text-muted-foreground ml-auto">(actual)</span>
                )}
              </Button>
            ))}
            <Button
              variant={moveTargetFolder === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMoveTargetFolder(null)}
              disabled={!moveTarget?.currentFolderId}
              className="w-full gap-2 justify-start"
              data-testid="move-folder-none"
            >
              <Briefcase className="w-4 h-4" />
              Sin carpeta
              {!moveTarget?.currentFolderId && (
                <span className="text-xs text-muted-foreground ml-auto">(actual)</span>
              )}
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMoveProjectDialog(false); setMoveTarget(null); }}>
              Cancelar
            </Button>
            <Button
              onClick={handleMoveProject}
              disabled={moveTargetFolder === moveTarget?.currentFolderId || (moveTargetFolder === null && !moveTarget?.currentFolderId)}
              data-testid="move-project-confirm-btn"
            >
              Mover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
